<script setup lang="ts">
  import type { NScrollbar } from 'naive-ui'

  import { keepPreviousData } from '@tanstack/vue-query'

  import type { UserProfileOut } from '@/shared/api/intranet'
  import { usePagination } from '@/shared/lib'
  import { InfiniteScrollElement, PageDecorationImage } from '@/shared/ui/components'

  import { listUserProfilesPage } from '@/entities/profile'

  import { useThanksDialog } from '@/features/profile/sendThanks'

  import { ProfilesItem, ProfilesListSearchBar, ProfilesNotFound } from '@/views/profile'

  definePage({
    meta: { layout: 'baseNew' },
  })

  const { t } = useI18n()

  useHead({
    title: () => t('admin_employees_title'),
  })

  const { query, order } = usePagination('/profiles')
  const route = useRoute('/profiles')

  if (route.query.search) {
    query.value = route.query.search as string
  }

  const { data, isFetching, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['profilesList', query, order],
    queryFn: ({ signal, pageParam }) =>
      listUserProfilesPage({
        page: {
          number: pageParam,
          size: 20,
        },
        filter: {
          query: query.value,
          is_blocked: false,
        },
        signal,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      const totalPages = lastPage?.totalPages
      if (!totalPages || totalPages <= lastPageParam) {
        return undefined
      }

      return lastPageParam + 1
    },
    select: (response): UserProfileOut[] => {
      return response.pages.flatMap((pageItem) => pageItem.userProfiles ?? [])
    },
    placeholderData: keepPreviousData,
  })

  const userProfiles = computed<UserProfileOut[]>(() => data.value || [])

  const showNotFound = computed(() => !userProfiles.value.length && !isFetching.value && Boolean(query.value.length))

  const { openThanksDialog } = useThanksDialog()

  const updateQuery = (newVal: string) => (query.value = newVal)

  const isSkeletonShows = computed(() => isLoading.value || (isFetching.value && query.value))
  const scrollbarRef = useTemplateRef<typeof NScrollbar>('scrollbarRef')
  const scrollbarContainerEl = computed(() => scrollbarRef.value?.scrollbarInstRef?.containerRef)
</script>

<template>
  <div class="wide-responsive h-screen-content max-h-screen-content flex w-full flex-col overflow-hidden">
    <div class="relative w-full shrink-0 border-b">
      <PageDecorationImage />

      <div class="p-6 pt-8">
        <div class="heading-md mb-6">
          {{ $t('navigation_item_employees') }}
        </div>
        <ProfilesListSearchBar
          v-model:order="order"
          :query="query"
          @update:model-value="updateQuery"
        />
      </div>
    </div>

    <div class="min-h-0 flex-1">
      <NScrollbar
        ref="scrollbarRef"
        class="h-full"
      >
        <InfiniteScrollElement
          v-if="scrollbarContainerEl"
          class="mx-auto min-h-155 w-full max-w-225 px-6"
          :class="{
            'divide-border divide-y': !showNotFound && !isSkeletonShows,
          }"
          :scrollable-element="scrollbarContainerEl"
          :can-load="hasNextPage && !isFetchingNextPage"
          @load="fetchNextPage"
        >
          <template v-if="isSkeletonShows">
            <NSkeleton
              v-for="idx in 6"
              :key="idx"
              :sharp="false"
              height="112px"
              class="mb-1"
            />
          </template>

          <ProfilesNotFound v-else-if="showNotFound" />

          <template v-else>
            <ProfilesItem
              v-for="item in userProfiles"
              :key="item.id"
              :item="item"
              @thank="openThanksDialog(item)"
            />
          </template>
          <template #loader>
            <div
              v-if="isFetchingNextPage"
              class="flex justify-center pt-6"
            >
              <NSpin
                size="large"
                show
              />
            </div>
          </template>
        </InfiniteScrollElement>
      </NScrollbar>
    </div>
  </div>
</template>
